import React, { useState, useEffect, Fragment, useRef } from 'react';

import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import {
  Table, Checkbox, Popconfirm, Select,
} from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import H3Text from '@Uilib/h3Text';
import ProductActions from '@Actions/productActions';
import { QUANTITY } from '@Apis/constants';
import H3FormInput from '@Uilib/h3FormInput';
import H3Button, { defaultButtonTypes } from '@Uilib/h3Button';
import CategoryActions from '@Actions/categoryActions';
import CategoryFilterNew from '../../../../Common/CategoryFilterNew';
import useClickOutside from '../../../../../hooks/useClickOutside';
import './style.scss';
import PRZSelect from '../../../../Common/UI/PRZSelect';

const { Option } = Select;

function BulkUpdateProducts({
  user, selectedRows, bulkUpdateProductsLoading, bulkUpdateProducts, callback, getCategories,
}) {
  const [selectedProducts, setSelectedProducts] = useState([]);

  const outSideClosing = useRef(null);
  useClickOutside(outSideClosing);

  const precision = user?.tenant_info?.global_config?.settings?.price_precision || 2;

  const updateRow = (key, field, value) => {
    const productsCopy = JSON.parse(JSON.stringify(selectedProducts));
    for (let i = 0; i < selectedProducts?.length; i++) {
      if (productsCopy[i]?.key === key) {
        productsCopy[i][field] = value;
      }
    }
    setSelectedProducts(productsCopy);
  };

  const getProductColumns = () => {
    const columns = [
      {
        title: 'Product',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        width: '220px',
        render: (text, record) => (
          <Fragment>
            <H3Text text={`#${record?.internal_sku_code}`} className="table-subscript" />
            <H3Text text={record?.product_sku_name} className="prod-table__text" />
          </Fragment>
        ),
        fixed: 'left',
      },
      {
        title: 'Internal SKU Code',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        width: '180px',
        render: (text, record) => (
          <H3FormInput
            name="Internal Reference Id"
            type="text"
            inputClassName="orgFormInput"
            placeholder=""
            onChange={(e) => updateRow(record.key, 'ref_product_code', e.target.value)}
            value={record?.ref_product_code}
          />
        ),
      },
      {
        title: 'Product Type',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        width: '120px',
        render: (text, record) => (
          <Fragment>
            <PRZSelect
              ref={outSideClosing}
              value={record?.product_type}
              disabled={bulkUpdateProductsLoading}
              onChange={(value) => updateRow(record.key, 'product_type', value)}
            >
              <Option key="STORABLE" value="STORABLE">Storable</Option>
              <Option key="NON_STORABLE" value="NON_STORABLE">Non Storable</Option>
              <Option key="BUNDLE" value="BUNDLE">Bundle</Option>
              <Option key="SERVICE" value="SERVICE">Service</Option>
            </PRZSelect>
          </Fragment>
        ),
      },
      {
        title: 'Category',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        width: '120px',
        render: (text, record) => (
          <Fragment>
            <CategoryFilterNew
              onChange={(value) => updateRow(record?.key, 'product_category_id', value)}
              selectedCategory={record?.product_category_id || record?.product_category_info?.product_category_id}
              disabled={bulkUpdateProductsLoading}
              hideTitle
              noApiCall
              multiple
            />
          </Fragment>
        ),
      },
      {
        title: 'Barcode',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        width: '150px',
        render: (text, record) => (
          <H3FormInput
            name="Barcode"
            type="text"
            inputClassName="orgFormInput"
            placeholder=""
            onChange={(e) => updateRow(record.key, 'barcode', e.target.value)}
            value={record?.barcode}
          />
        ),
        hidden: !user?.tenant_info?.global_config?.sub_modules?.barcoding?.is_active,
      },
      {
        title: 'HSN Code',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        width: '150px',
        render: (text, record) => (
          <H3FormInput
            name="HSN Code"
            type="text"
            inputClassName="orgFormInput"
            placeholder=""
            onChange={(e) => updateRow(record.key, 'hsn_code', e.target.value)}
            value={record?.hsn_code}
          />
        ),
      },
      {
        title: 'Shelf Life',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        width: '150px',
        render: (text, record) => (
          <div className="product-form__shelf-life">
            <input
              min="0"
              onWheel={(event) => event.target.blur()}
              name="expiry days"
              type="number"
              className="product-form__shelf-life-input"
              onChange={(event) => updateRow(record.key, 'expiry_days', event.target.value)}
              value={record?.expiry_days}
              disabled={bulkUpdateProductsLoading}
            />
            <PRZSelect
              ref={outSideClosing}
              filterOption={false}
              defaultValue="DAYS"
              value={record.expiry_duration}
              onChange={(value) => updateRow(record.key, 'expiry_duration', value)}
            >
              <Option key="DAYS" value="DAYS">Days</Option>
              <Option key="MONTHS" value="MONTHS">Months</Option>
            </PRZSelect>
          </div>
        ),
      },
      {
        title: 'Reorder Level',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        width: '150px',
        render: (text, record) => (
          <H3FormInput
            name="Reorder Level"
            type="text"
            inputClassName="orgFormInput"
            placeholder=""
            onChange={(e) => updateRow(record.key, 'threshold_qty', e.target.value)}
            value={record?.threshold_qty}
          />
        ),
      },
      {
        title: 'Sale',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        width: '80px',
        render: (text, record) => (
          <Checkbox checked={record?.is_sales_product} onChange={(e) => updateRow(record.key, 'is_sales_product', !record.is_sales_product)} />
        ),
        className: 'bulk-import-padded-cell',
      },
      {
        title: 'Purchase',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        width: '80px',
        render: (text, record) => (
          <Checkbox checked={record?.is_purchase_product} onChange={(e) => updateRow(record.key, 'is_purchase_product', !record.is_purchase_product)} />
        ),
        className: 'bulk-import-padded-cell',
      },
      {
        title: 'Cost Price',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        width: '120px',
        render: (text, record) => (
          <H3FormInput
            name="Cost Price"
            type="number"
            inputClassName="orgFormInput"
            placeholder=""
            onChange={(e) => updateRow(record.key, 'cost_price', QUANTITY(e.target.value, precision))}
            value={QUANTITY(record?.cost_price, precision)}
          />
        ),
      },
      {
        title: 'Selling Price',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        width: '120px',
        render: (text, record) => (
          <H3FormInput
            name="Selling Price"
            type="number"
            inputClassName="orgFormInput"
            placeholder=""
            onChange={(e) => updateRow(record.key, 'selling_price', QUANTITY(e.target.value, precision))}
            value={QUANTITY(record?.selling_price, precision)}
          />
        ),
      },
      {
        title: 'MRP',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        width: '120px',
        render: (text, record) => (
          <H3FormInput
            name="MRP"
            type="text"
            inputClassName="orgFormInput"
            placeholder=""
            onChange={(e) => updateRow(record.key, 'mrp', e.target.value)}
            value={record?.mrp}
          />
        ),
      },
    ].filter((item) => !item.hidden);
    columns.push({
      title: '',
      render: (text, record) => (
        <Fragment>
          <Fragment>
            <Popconfirm
              title="Are you sure you want to remove this product?"
              onConfirm={() => setSelectedProducts(selectedProducts?.filter((item) => item?.key !== record?.key))}
              onCancel={() => {}}
              okText="Yes"
              cancelText="No"
            >
              <div className="calculate-sp__delete-button">
                <CloseOutlined />
              </div>
            </Popconfirm>
          </Fragment>
        </Fragment>
      ),
      width: '40px',
      fixed: 'right',
      className: 'bulk-import-padded-cell',
    });
    return columns;
  };

  useEffect(() => {
    getCategories();
    setSelectedProducts(selectedRows?.map((item) => ({
      ...item,
      product_sku_id: item?.product_info?.product_sku_id,
      internal_sku_code: item?.product_info?.internal_sku_code,
      product_sku_name: item?.product_info?.product_sku_name,
      expiry_days: item?.product_info?.expiry_days !== -1 ? item?.product_info?.expiry_days > 60 ? (item?.product_info?.expiry_days / 30).toFixed(2) : item?.product_info?.expiry_days : null,
      expiry_duration: item?.product_info?.expiry_days > 60 ? 'MONTHS' : 'DAYS',
      threshold_qty: item?.threshold_qty,
      hsn_code: item?.product_info?.hsn_code,
      product_info: null,
    })));
  }, [selectedRows]);

  /**
     *
     * @return {JSX.Element}
     */
  return (
    <Fragment>
      <div className="bulk-upload-products__wrapper">
        <Table
          showHeader
          size="small"
          columns={getProductColumns()}
          bordered
          dataSource={selectedProducts || []}
          pagination={false}
          scroll={{ x: 'max-content', y: 'calc(100dvh - 140px)' }}
        />
      </div>
      <div className="custom-drawer__footer" style={{ width: 'calc(100% - 50px)', display: 'flex', padding: '10px 0px !important' }}>
        <H3Button
          buttonType={defaultButtonTypes.BLUE_ROUNDED}
          text="Update Products"
          onClick={() => bulkUpdateProducts({
            tenant_products: selectedProducts?.map((item) => ({
              product_sku_id: item?.product_sku_id,
              tenant_id: user?.tenant_info?.tenant_id,
              product_type: item?.product_type,
              product_category_id: item?.product_category_id || item?.product_category_info?.product_category_id,
              cost_price: item?.cost_price || 0,
              selling_price: item?.selling_price || 0,
              mrp: item?.mrp || 0,
              ref_product_code: item?.ref_product_code,
              barcode: item?.barcode,
              is_sales_product: item?.is_sales_product || false,
              is_purchase_product: item?.is_purchase_product || false,
              threshold_qty: item?.threshold_qty,
              hsn_code: item?.hsn_code,
              expiry_days: item?.expiry_days ? item?.expiry_duration === 'MONTHS' ? parseInt(Number(item?.expiry_days) * 30) : Number(item?.expiry_days) : -1,
            })),
          }, () => {
            callback();
          })}
          isLoading={bulkUpdateProductsLoading}
          disabled={bulkUpdateProductsLoading}
          style={{
            width: '200px',
            borderRadius: '5px',
            padding: '7px 0px',
            marginLeft: '0',
          }}
        />
      </div>
    </Fragment>
  );
}

const mapStateToProps = ({
  UserReducers, ProductReducers,
}) => ({
  user: UserReducers.user,
  MONEY: UserReducers.MONEY,
  bulkUpdateProductsLoading: ProductReducers.bulkUpdateProductsLoading,
});

const mapDispatchToProps = (dispatch) => ({
  bulkUpdateProducts: (payload, callback) => dispatch(ProductActions.bulkUpdateProducts(payload, callback)),
  getCategories: (orgId, page, limit, searchKeyword) => dispatch(CategoryActions.getCategories(orgId, page, limit, searchKeyword)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(BulkUpdateProducts));
